const DAY_MS = 24 * 60 * 60 * 1000;

export const FSRS_RATING = Object.freeze({
  AGAIN: 1,
  HARD: 2,
  GOOD: 3,
  EASY: 4,
});

export const FSRS_LABELS = Object.freeze({
  [FSRS_RATING.AGAIN]: "Again",
  [FSRS_RATING.HARD]: "Hard",
  [FSRS_RATING.GOOD]: "Good",
  [FSRS_RATING.EASY]: "Easy",
});

export const DEFAULT_FSRS_PARAMS = Object.freeze({
  requestRetention: 0.9,
  maximumInterval: 36500,
  againIntervalMinutes: 10,
  w: [
    0.4072, 1.1829, 3.1262, 15.4722, 7.2102, 0.5316, 1.0651, 0.0234, 1.616,
    0.1544, 1.0824, 1.9813, 0.0953, 0.2975, 2.2042, 0.2407, 2.9466,
  ],
});

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const mapFsrsRating = (input) => {
  if (typeof input === "number" && Number.isFinite(input)) {
    return clamp(Math.round(input), FSRS_RATING.AGAIN, FSRS_RATING.EASY);
  }
  const normalized = String(input || "")
    .trim()
    .toLowerCase();
  if (normalized === "again") return FSRS_RATING.AGAIN;
  if (normalized === "hard") return FSRS_RATING.HARD;
  if (normalized === "good") return FSRS_RATING.GOOD;
  if (normalized === "easy") return FSRS_RATING.EASY;
  const numeric = Number(normalized);
  if (Number.isFinite(numeric)) {
    return clamp(Math.round(numeric), FSRS_RATING.AGAIN, FSRS_RATING.EASY);
  }
  return FSRS_RATING.GOOD;
};

export const createFsrsState = ({ nowMs = Date.now() } = {}) => ({
  stability: null,
  difficulty: null,
  reps: 0,
  lapses: 0,
  lastReviewAt: null,
  state: "new",
  createdAt: nowMs,
});

const initDifficulty = (rating, params) =>
  clamp(params.w[4] - params.w[5] * (rating - 3), 1, 10);

const initStability = (rating, params) => {
  const r = rating;
  const base =
    params.w[0] +
    params.w[1] * r +
    params.w[2] * r * r +
    params.w[3] * r * r * r;
  return Math.max(0.1, base);
};

const meanReversion = (init, current, factor) => init + factor * (current - init);

const nextDifficulty = (difficulty, rating, params) => {
  const next = difficulty + params.w[6] * (rating - 3);
  const reverted = meanReversion(initDifficulty(3, params), next, params.w[7]);
  return clamp(reverted, 1, 10);
};

const calcRetrievability = (elapsedDays, stability, retention) => {
  if (!stability || stability <= 0) return 0;
  return Math.exp(Math.log(retention) * (elapsedDays / stability));
};

const nextStability = (difficulty, stability, rating, retrievability, params) => {
  if (rating === FSRS_RATING.AGAIN) {
    const updated =
      params.w[11] *
      Math.pow(difficulty, -params.w[12]) *
      Math.pow(1 - retrievability, params.w[13]);
    return Math.max(0.1, updated);
  }

  const hardPenalty = rating === FSRS_RATING.HARD ? params.w[15] : 1;
  const easyBonus = rating === FSRS_RATING.EASY ? params.w[16] : 1;
  const growth =
    Math.exp(params.w[8]) *
    (11 - difficulty) *
    Math.pow(stability, -params.w[9]) *
    (Math.exp((1 - retrievability) * params.w[10]) - 1);
  return Math.max(0.1, stability * (1 + growth * hardPenalty * easyBonus));
};

const nextIntervalDays = (stability, params) => {
  const interval =
    stability * (Math.log(params.requestRetention) / Math.log(0.9));
  if (!Number.isFinite(interval) || interval <= 0) return 1;
  return clamp(Math.round(interval), 1, params.maximumInterval);
};

export const scheduleFsrsReview = ({
  card = {},
  rating,
  nowMs = Date.now(),
  params = DEFAULT_FSRS_PARAMS,
} = {}) => {
  const ratingValue = mapFsrsRating(rating);
  const ratingLabel = FSRS_LABELS[ratingValue];

  const current = card.fsrs || createFsrsState({ nowMs });
  const lastReviewAt = current.lastReviewAt || card.lastReviewedAt || null;
  const elapsedDays = lastReviewAt
    ? Math.max(0, (nowMs - lastReviewAt) / DAY_MS)
    : 0;

  let stability = current.stability;
  let difficulty = current.difficulty;
  let reps = current.reps || 0;
  let lapses = current.lapses || 0;
  let state = current.state || "new";

  if (!stability || !difficulty || reps === 0) {
    stability = initStability(ratingValue, params);
    difficulty = initDifficulty(ratingValue, params);
    reps = 1;
    state = "learning";
    if (ratingValue === FSRS_RATING.AGAIN) {
      lapses += 1;
    }
  } else {
    const retrievability = calcRetrievability(
      elapsedDays,
      stability,
      params.requestRetention
    );
    stability = nextStability(
      difficulty,
      stability,
      ratingValue,
      retrievability,
      params
    );
    difficulty = nextDifficulty(difficulty, ratingValue, params);
    reps += 1;
    state = ratingValue === FSRS_RATING.AGAIN ? "relearning" : "review";
    if (ratingValue === FSRS_RATING.AGAIN) {
      lapses += 1;
    }
  }

  const intervalDays =
    ratingValue === FSRS_RATING.AGAIN
      ? params.againIntervalMinutes / (60 * 24)
      : nextIntervalDays(stability, params);
  const dueAt =
    ratingValue === FSRS_RATING.AGAIN
      ? nowMs + params.againIntervalMinutes * 60 * 1000
      : nowMs + intervalDays * DAY_MS;

  return {
    dueAt,
    intervalDays,
    ratingValue,
    ratingLabel,
    fsrs: {
      stability,
      difficulty,
      reps,
      lapses,
      lastReviewAt: nowMs,
      state,
      updatedAt: nowMs,
    },
  };
};

export default scheduleFsrsReview;
